import React from 'react';
import PropTypes from 'prop-types';
import {computed, observable, reaction} from 'mobx';
import {observer, inject} from 'mobx-react';
import LazyLoad, { forceCheck } from 'react-lazyload';
import {withConfig, withI18n} from 'spa/Components/Context';

const HIGH_RES_SCREEN_MEDIA_QUERY = 'only screen and (-webkit-min-device-pixel-ratio: 2), only screen and (min--moz-device-pixel-ratio: 2), only screen and (-o-min-device-pixel-ratio: 2/1), only screen and (min-device-pixel-ratio: 2), only screen and (min-resolution: 192dpi), only screen and (min-resolution: 2dppx)';

@inject(({
  store,
  store: {
    initialLoadComplete,
    isAmpPage,
    screen,
  },
}) => (
  {
    initialLoadComplete,
    isAmpPage,
    screen,
    store,
  }
))
@observer
@withConfig
@withI18n
class ProgressiveImage extends React.Component {
  // Proptypes info:
  //
  // disableWebp - disable use of webp images
  // highResImg - normal image URL
  // highResImg2x - @2x image URL
  // lowResImg - low quality image url used for pre-render

  static propTypes = {
    altText: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    disableWebp: PropTypes.bool,
    initialLoadComplete: PropTypes.bool,
    isAmpPage: PropTypes.bool,
    height: PropTypes.string,
    highResImg: PropTypes.string.isRequired,
    highResImg2x: PropTypes.string,
    layout: PropTypes.string,
    lowResImg: PropTypes.string.isRequired,
    screen: PropTypes.shape({
      supportsWebp: PropTypes.bool,
      width: PropTypes.number,
    }),
    store: PropTypes.shape({
      initialLoadComplete: PropTypes.bool,
      isAmpPage: PropTypes.bool,
    }),
    width: PropTypes.string,
  };

  UNSAFE_componentWillMount() {
    const {initialLoadComplete} = this.props;

    this.selectedHighRes = this.props.highResImg;
    this.selectedHighRes2x = this.props.highResImg2x;

    if (typeof document !== 'undefined' && document.readyState === 'complete' && !initialLoadComplete) {
      this.props.store.initialLoadComplete = true;
    }
  }

  componentDidMount() {
    this.listeners = [
      reaction(
        () => this.props.initialLoadComplete,
        (initialLoadComplete) => {
          if (initialLoadComplete) {
            this.showHighRes = true;
            forceCheck();
          }
        },
        {
          fireImmediately: true,
          name: 'Check if content has finished loading',
        },
      ),
      reaction(
        () => this.props.screen.supportsWebp,
        (supportsWebp) => {
          if (!this.props.disableWebp && supportsWebp) {
            this.selectedHighRes = this.getWebpImage(this.props.highResImg);
            this.selectedHighRes2x = this.props.highResImg2x && this.getWebpImage(this.props.highResImg2x);
          }
        },
        {
          fireImmediately: true,
          name: 'Get image in webp',
        },
      ),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  // Get image url in webp format
  getWebpImage = (url) => {
    const imageFilename = url.substring(url.lastIndexOf('/') + 1);

    const imageExtension = imageFilename.substring(imageFilename.lastIndexOf('.') + 1);

    return url.replace(imageExtension, 'webp');
  }

  @observable showHighRes = false;
  @observable supportsWebp = false;
  @observable selectedHighRes;
  @observable selectedHighRes2x;

  @computed get imageUrl() {
    const {lowResImg} = this.props;
    const {
      selectedHighRes, selectedHighRes2x, showHighRes, use2xImage,
    } = this;

    if (!showHighRes) return lowResImg;

    if (!selectedHighRes) return null;

    return (use2xImage && selectedHighRes2x) ? selectedHighRes2x : selectedHighRes;
  }

  @computed get use2xImage() {
    if (typeof window === 'undefined') return null;

    const {showHighRes} = this;

    return showHighRes && window.matchMedia(HIGH_RES_SCREEN_MEDIA_QUERY).matches;
  }

  render() {
    const {imageUrl} = this;

    const {
      altText,
      disableWebp,
      initialLoadComplete,
      height,
      highResImg,
      highResImg2x,
      lowResImg,
      isAmpPage,
      screen,
      width,
      ...rest
    } = this.props;

    return isAmpPage ?
      (
        <div className={this.props.className}>
          <amp-img alt={altText} height={height} layout={this.props.layout || 'fixed'} src={highResImg2x || highResImg} width={width} />
        </div>
      ) : (
        <LazyLoad
          placeholder={
            <span />
          }
          offset={100}>
          <img src={imageUrl} alt={altText} width={width} {...rest} />
        </LazyLoad>
      );
  }
}

export default ProgressiveImage;



// WEBPACK FOOTER //
// ./spa/Components/ProgressiveImage.jsx